import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ConfigurationPanel = ({ 
  selectedComponent, 
  onParameterChange, 
  onComponentUpdate,
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState('parameters');

  if (!selectedComponent) {
    return (
      <div className="w-80 bg-card border-l border-border flex items-center justify-center">
        <div className="text-center p-6">
          <Icon name="Settings" size={32} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-medium text-foreground mb-2">No Component Selected</h3>
          <p className="text-sm text-muted-foreground">
            Select a component on the canvas to configure its parameters and settings.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'parameters', name: 'Parameters', icon: 'Sliders' },
    { id: 'inputs', name: 'Inputs', icon: 'ArrowRight' },
    { id: 'outputs', name: 'Outputs', icon: 'ArrowLeft' },
    { id: 'advanced', name: 'Advanced', icon: 'Settings' }
  ];

  const getParameterInput = (param, value) => {
    switch (param?.type) {
      case 'number':
        return (
          <Input
            type="number"
            value={value || param?.default || ''}
            onChange={(e) => onParameterChange(param?.key, parseFloat(e?.target?.value))}
            placeholder={param?.placeholder}
            min={param?.min}
            max={param?.max}
            step={param?.step}
          />
        );
      
      case 'select':
        return (
          <Select
            options={param?.options?.map(opt => ({ value: opt?.value, label: opt?.label }))}
            value={value || param?.default || ''}
            onChange={(val) => onParameterChange(param?.key, val)}
            placeholder={param?.placeholder}
          />
        );
      
      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={value !== undefined ? value : param?.default}
              onChange={(e) => onParameterChange(param?.key, e?.target?.checked)}
              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-ring"
            />
            <span className="text-sm text-foreground">{param?.label}</span>
          </div>
        );
      
      case 'text':
      default:
        return (
          <Input
            type="text"
            value={value || param?.default || ''}
            onChange={(e) => onParameterChange(param?.key, e?.target?.value)}
            placeholder={param?.placeholder}
          />
        );
    }
  };

  const getComponentParameters = () => {
    // Mock parameters based on component type
    const baseParams = [
      {
        key: 'enabled',
        label: 'Enabled',
        type: 'boolean',
        default: true,
        description: 'Enable or disable this component'
      }
    ];

    switch (selectedComponent?.id) {
      case 'moving-average':
        return [
          ...baseParams,
          {
            key: 'period',
            label: 'Period',
            type: 'number',
            default: 20,
            min: 1,
            max: 200,
            description: 'Number of periods for calculation'
          },
          {
            key: 'type',
            label: 'MA Type',
            type: 'select',
            default: 'SMA',
            options: [
              { value: 'SMA', label: 'Simple Moving Average' },
              { value: 'EMA', label: 'Exponential Moving Average' },
              { value: 'WMA', label: 'Weighted Moving Average' }
            ],
            description: 'Type of moving average calculation'
          }
        ];
      
      case 'rsi':
        return [
          ...baseParams,
          {
            key: 'period',
            label: 'Period',
            type: 'number',
            default: 14,
            min: 2,
            max: 100,
            description: 'RSI calculation period'
          },
          {
            key: 'overbought',
            label: 'Overbought Level',
            type: 'number',
            default: 70,
            min: 50,
            max: 90,
            description: 'RSI overbought threshold'
          },
          {
            key: 'oversold',
            label: 'Oversold Level',
            type: 'number',
            default: 30,
            min: 10,
            max: 50,
            description: 'RSI oversold threshold'
          }
        ];
      
      case 'buy-order': case'sell-order':
        return [
          ...baseParams,
          {
            key: 'orderType',
            label: 'Order Type',
            type: 'select',
            default: 'MARKET',
            options: [
              { value: 'MARKET', label: 'Market Order' },
              { value: 'LIMIT', label: 'Limit Order' },
              { value: 'STOP', label: 'Stop Order' },
              { value: 'STOP_LIMIT', label: 'Stop Limit Order' }
            ],
            description: 'Type of order to execute'
          },
          {
            key: 'quantity',
            label: 'Quantity',
            type: 'number',
            default: 1,
            min: 0.01,
            step: 0.01,
            description: 'Order quantity'
          },
          {
            key: 'priceOffset',
            label: 'Price Offset (%)',
            type: 'number',
            default: 0,
            min: -10,
            max: 10,
            step: 0.1,
            description: 'Price offset from market price'
          }
        ];
      
      default:
        return baseParams;
    }
  };

  const parameters = getComponentParameters();
  const currentValues = selectedComponent?.parameters || {};

  const renderParametersTab = () => (
    <div className="space-y-4">
      {parameters?.map(param => (
        <div key={param?.key} className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            {param?.label}
          </label>
          {getParameterInput(param, currentValues?.[param?.key])}
          {param?.description && (
            <p className="text-xs text-muted-foreground">{param?.description}</p>
          )}
        </div>
      ))}
    </div>
  );

  const renderInputsTab = () => (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-foreground">Input Connections</h4>
      {selectedComponent?.inputs && selectedComponent?.inputs?.length > 0 ? (
        selectedComponent?.inputs?.map((input, index) => (
          <div key={index} className="flex items-center gap-3 p-3 bg-background rounded-md border border-border">
            <div className="w-3 h-3 bg-accent rounded-full" />
            <div className="flex-1">
              <div className="text-sm font-medium text-foreground">{input}</div>
              <div className="text-xs text-muted-foreground">
                {selectedComponent?.connectedInputs?.[index] ? 'Connected' : 'Not connected'}
              </div>
            </div>
          </div>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">No inputs available</p>
      )}
    </div>
  );

  const renderOutputsTab = () => (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-foreground">Output Connections</h4>
      {selectedComponent?.outputs && selectedComponent?.outputs?.length > 0 ? (
        selectedComponent?.outputs?.map((output, index) => (
          <div key={index} className="flex items-center gap-3 p-3 bg-background rounded-md border border-border">
            <div className="flex-1">
              <div className="text-sm font-medium text-foreground">{output}</div>
              <div className="text-xs text-muted-foreground">
                {selectedComponent?.connectedOutputs?.[index] ? 'Connected' : 'Not connected'}
              </div>
            </div>
            <div className="w-3 h-3 bg-primary rounded-full" />
          </div>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">No outputs available</p>
      )}
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Component Name
        </label>
        <Input
          value={selectedComponent?.customName || selectedComponent?.name}
          onChange={(e) => onComponentUpdate(selectedComponent?.instanceId, { customName: e?.target?.value })}
          placeholder="Enter custom name"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Description
        </label>
        <textarea
          value={selectedComponent?.customDescription || ''}
          onChange={(e) => onComponentUpdate(selectedComponent?.instanceId, { customDescription: e?.target?.value })}
          placeholder="Add component description..."
          className="w-full p-2 bg-background border border-border rounded-md text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Execution Priority
        </label>
        <Select
          options={[
            { value: 'high', label: 'High Priority' },
            { value: 'normal', label: 'Normal Priority' },
            { value: 'low', label: 'Low Priority' }
          ]}
          value={selectedComponent?.priority || 'normal'}
          onChange={(val) => onComponentUpdate(selectedComponent?.instanceId, { priority: val })}
        />
      </div>

      <div className="pt-4 border-t border-border">
        <h4 className="text-sm font-medium text-foreground mb-3">Component Info</h4>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Instance ID:</span>
            <span className="font-mono text-foreground">{selectedComponent?.instanceId}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Type:</span>
            <span className="text-foreground">{selectedComponent?.category}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status:</span>
            <span className={`font-medium ${
              selectedComponent?.status === 'active' ? 'text-success' :
              selectedComponent?.status === 'error'? 'text-error' : 'text-muted-foreground'
            }`}>
              {selectedComponent?.status || 'inactive'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">Configuration</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={16} />
          </Button>
        </div>
        
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-8 h-8 ${selectedComponent?.color} rounded-md flex items-center justify-center`}>
            <Icon name={selectedComponent?.icon} size={16} color="white" />
          </div>
          <div>
            <h3 className="font-medium text-foreground">{selectedComponent?.customName || selectedComponent?.name}</h3>
            <p className="text-xs text-muted-foreground">{selectedComponent?.category}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {tabs?.map(tab => (
            <Button
              key={tab?.id}
              variant={activeTab === tab?.id ? "default" : "ghost"}
              size="xs"
              onClick={() => setActiveTab(tab?.id)}
              className="text-xs"
            >
              <Icon name={tab?.icon} size={12} className="mr-1" />
              {tab?.name}
            </Button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'parameters' && renderParametersTab()}
        {activeTab === 'inputs' && renderInputsTab()}
        {activeTab === 'outputs' && renderOutputsTab()}
        {activeTab === 'advanced' && renderAdvancedTab()}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onComponentUpdate(selectedComponent?.instanceId, { status: 'active' })}
            className="flex-1"
          >
            <Icon name="Play" size={14} className="mr-1" />
            Test
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => {/* Save configuration */}}
            className="flex-1"
          >
            <Icon name="Save" size={14} className="mr-1" />
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPanel;